import { graphql } from './gql';

/**
 * Before saving, arqam says whether this reading needs a photo of the meter
 * (its docs 46–47): an unusual consumption (more than 30 m³ and more than 3×
 * the meter's average; more than 60 m³ for a newer meter), or every reading
 * when the committee turned «تصوير العداد مع كل قراءة» on. The save checks
 * again.
 */
export const ReadingCheckDocument = graphql(`
  query ReadingCheck(
    $meterId: UUID!
    $reading: Int!
    $period: Date!
    $billId: UUID
  ) {
    readingCheck(
      meterId: $meterId
      reading: $reading
      period: $period
      billId: $billId
    ) {
      consumption
      usualConsumption
      photoRequired
      reason
    }
  }
`);

/**
 * The committee's switch «تصوير العداد مع كل قراءة»: when on, the camera step
 * is shown from the start instead of after the first check.
 */
export const ReadingPhotoSettingsDocument = graphql(`
  query ReadingPhotoSettings {
    currentSettings {
      readingPhotoAlways
    }
  }
`);

/** Why the camera step is open (from readingCheck or the save's error). */
export type PhotoRequest = {
  reason: 'UNUSUAL' | 'ALWAYS';
  consumption: number | null;
  /** The meter's average; null for a meter with fewer than 3 bills. */
  usualConsumption: number | null;
};

const m3 = (n: number) =>
  `${n.toLocaleString('ar-MA', { maximumFractionDigits: 1 })} م³`;

/** What the reader is asked, in the committee's words. */
export function photoRequestMessage(request: PhotoRequest): string {
  if (request.reason === 'ALWAYS') {
    return 'اللجنة تطلب صورة العداد مع كل قراءة.';
  }
  const consumption =
    request.consumption === null ? '' : `: ${m3(request.consumption)}`;
  if (request.usualConsumption === null) {
    return `استهلاك مرتفع لعداد جديد${consumption}. المرجو تصوير العداد بحيث يظهر الرقم بوضوح.`;
  }
  return `استهلاك غير معتاد${consumption} (المعتاد ${m3(request.usualConsumption)}). المرجو تصوير العداد بحيث يظهر الرقم بوضوح.`;
}

/**
 * A photo request from readingCheck's answer or from the save's
 * READING_PHOTO_REQUIRED values; null when no photo is needed.
 */
export function toPhotoRequest(values: {
  photoRequired?: boolean;
  reason?: unknown;
  consumption?: unknown;
  usualConsumption?: unknown;
}): PhotoRequest | null {
  if (values.photoRequired === false) return null;
  const reason = values.reason === 'ALWAYS' ? 'ALWAYS' : 'UNUSUAL';
  const number = (v: unknown) =>
    v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v);
  return {
    reason,
    consumption: number(values.consumption),
    usualConsumption: number(values.usualConsumption),
  };
}
